import React, { useState } from 'react';

import { Stack, Typography } from '@mui/material';
import { FolderCloud, Paperclip2 } from 'iconsax-reactjs';
import { useTranslation } from 'react-i18next';

import { DragDropBoxStyled, UploadButtonStyled } from './styles';

interface IProps {
  onDrop: (event: React.DragEvent<HTMLDivElement>) => void;
  onFileSelect: (event: React.ChangeEvent<HTMLInputElement>) => void;
  isMultiple?: boolean;
  supportedFormats?: string;
  accept?: string;
}

const FormDocumentUpload = React.memo(
  ({
    onDrop,
    onFileSelect,
    isMultiple = true,
    supportedFormats = 'PDF, PNG, JPG, JPEG, DOC, DOCX, ZIP',
    accept = '.pdf,.png,.jpeg,.jpg,.doc,.docx,.zip',
  }: IProps) => {
    const [isDragActive, setIsDragActive] = useState(false);

    const { t } = useTranslation();

    const handleDragEnter = (event: React.DragEvent<HTMLDivElement>) => {
      event.preventDefault();

      setIsDragActive(true);
    };

    const handleDragLeave = (event: React.DragEvent<HTMLDivElement>) => {
      event.preventDefault();

      setIsDragActive(false);
    };

    const handleDrop = (event: React.DragEvent<HTMLDivElement>) => {
      event.preventDefault();

      setIsDragActive(false);

      onDrop(event);
    };

    return (
      <DragDropBoxStyled
        isDragActive={isDragActive}
        onDrop={handleDrop}
        onDragOver={(event) => event.preventDefault()}
        onDragEnter={handleDragEnter}
        onDragLeave={handleDragLeave}
      >
        <Stack justifyContent="center" alignItems="center" spacing={2}>
          <FolderCloud />

          <Stack spacing={1} alignItems="center">
            <Typography variant="body1" fontWeight="bold">
              {t('general.drop_files')}
            </Typography>

            <Typography
              sx={(theme) => ({
                fontSize: '0.875rem',
                lineHeight: '1rem',
                color: theme.palette.secondary[100],
              })}
            >
              {supportedFormats}
            </Typography>
          </Stack>

          <UploadButtonStyled
            variant="contained"
            color="primary"
            endIcon={<Paperclip2 size={16} />}
            component="label"
          >
            {t('buttons.browse_file')}

            <input type="file" multiple={isMultiple} accept={accept} onChange={onFileSelect} />
          </UploadButtonStyled>
        </Stack>
      </DragDropBoxStyled>
    );
  },
);

export default FormDocumentUpload;
